import { Formik, FormikProps } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { isNumber, isEmpty, isBoolean } from 'lodash'
import { ChangeEvent, ReactElement } from 'react'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { Select } from 'components/v2/Select'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Label } from 'components/v2/Label'
import Heading from 'components/v2/Heading'
import { SelectTags } from 'components/v2/SelectTags'
import { Switch } from 'components/v2/Switch'
import { SelectState } from 'features/Cities/components/SelectState'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CompanyLinesCheckboxes } from 'features/Lines/components/CompanyLinesCheckboxes'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { TYPE } from 'features/Sites/components/Type/constants'
import { typeMessages } from 'features/Sites/components/Type/messages'
import { EPaymentMethod } from 'features/Sites/types'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import { IFormikSite, INewSiteFormProps, INewSiteProps } from './types'
import { ICompany } from 'features/Companies/types'
import { IPlace } from 'features/Places/types'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { GTUserSuggestion } from 'gql/types.generated'
import { MultiUserAutocomplete } from 'features/Users/components/MultiUserAutocomplete'
import { EUserType } from 'features/Users/types'

const PAYMENT_METHOD_LIST = [EPaymentMethod.CASH, EPaymentMethod.CARD, EPaymentMethod.MIXED, EPaymentMethod.RESERVED]

const SITE_TYPE_LIST = [TYPE.FIXED, TYPE.MOBILE]

const messages = defineMessages({
  companyPlaceholder: {
    id: 'sites.form.company.placeholder',
    defaultMessage: 'Company'
  },
  namePlaceholder: {
    id: 'sites.form.name.placeholder',
    defaultMessage: 'Point of sale name'
  },
  typePlaceholder: {
    id: 'sites.form.type.placeholder',
    defaultMessage: 'Select the type of point of sale'
  },
  line1Placeholder: {
    id: 'sites.new.form.address.line1.placeholder',
    defaultMessage: 'Line 1'
  },
  line2Placeholder: {
    id: 'sites.new.form.address.line2.placeholder',
    defaultMessage: 'Line 2'
  },
  cityPlaceholder: {
    id: 'sites.new.form.address.city.placeholder',
    defaultMessage: 'City'
  },
  zipCodePlaceholder: {
    id: 'sites.new.form.address.zipCode.placeholder',
    defaultMessage: 'ZIP code'
  },
  defaultOriginPlaceholder: {
    id: 'sites.new.form.origin.placeholder',
    defaultMessage: 'Default origin'
  }
})

const closingThresholdMessages = defineMessages({
  0: {
    id: 'closingThreshold.0',
    defaultMessage: 'None'
  },
  1: {
    id: 'closingThreshold.1',
    defaultMessage: '1 Min'
  },
  5: {
    id: 'closingThreshold.5',
    defaultMessage: '5 Min'
  },
  10: {
    id: 'closingThreshold.10',
    defaultMessage: '10 Min'
  }
})

const paymentMethodMessages = defineMessages({
  [EPaymentMethod.CASH]: {
    id: 'dictionary.cash',
    defaultMessage: 'Cash'
  },
  [EPaymentMethod.CARD]: {
    id: 'dictionary.card',
    defaultMessage: 'Card'
  },
  [EPaymentMethod.MIXED]: {
    id: 'dictionary.mixed',
    defaultMessage: 'Mixed'
  },
  [EPaymentMethod.RESERVED]: {
    id: 'dictionary.reserved',
    defaultMessage: 'Reserved'
  }
})

const getFilteredPaymentMethod = (values: EPaymentMethod[]): EPaymentMethod[] => {
  return PAYMENT_METHOD_LIST.filter((payment) => {
    return values.indexOf(payment) === -1
  })
}

const NewSiteForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  setValues,
  handleSubmit,
  error,
  loading,
  isEdit
}: INewSiteFormProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const isAddressOptional = !(
    values.address.line1 ||
    values.address.line2 ||
    values.address.city ||
    values.address.zipCode ||
    values.address.stateCode
  )

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="sites.form.generalInformation.title" defaultMessage="General information" />
            </Heading.H5>
          </Column>
        </Row>
        {canSelectCompany(login.accessToken) && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={(value: Pick<ICompany, 'id' | 'name'>): void => {
                  setValues({
                    ...values,
                    company: value,
                    users: [],
                    settings: {
                      ...values.settings,
                      enabledCompanyLineIDs: []
                    }
                  } as IFormikSite)
                }}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company?.id}
                errors={errors.company?.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                disabled={isEdit}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column>
            <Label>
              <FormattedMessage id="sites.form.companyLines" defaultMessage="Lines" />
            </Label>
            {values.company.id ? (
              <CompanyLinesCheckboxes
                id="settings.enabledCompanyLineIDs"
                companyId={values.company.id}
                onChange={(companyLineIds: string[]): void => {
                  setFieldTouched('settings.enabledCompanyLineIDs', true)
                  setFieldValue('settings.enabledCompanyLineIDs', companyLineIds)
                }}
                companyLineIds={values.settings.enabledCompanyLineIDs}
                touched={touched.settings && touched.settings.enabledCompanyLineIDs}
                errors={errors.settings && errors.settings.enabledCompanyLineIDs}
              />
            ) : (
              <FormattedMessage id="sites.form.selectCompany" defaultMessage="Select company" />
            )}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
          <Column width="50%">
            <Select
              id="type"
              value={values.type}
              onChange={({ target: { value } }: ChangeEvent<HTMLSelectElement>): void => {
                setFieldValue('type', value)

                if (value === TYPE.FIXED) {
                  setFieldValue('settings.snacks.tripRequired', false)
                } else if (value === TYPE.MOBILE && values.settings.snacks.canSell) {
                  setFieldValue('settings.snacks.tripRequired', true)
                }
              }}
              onBlur={handleBlur}
              touched={touched.type}
              errors={errors.type}
              label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
            >
              <option value="">{intl.formatMessage(messages.typePlaceholder)}</option>
              {SITE_TYPE_LIST.map((type) => {
                return (
                  <option value={type} key={type}>
                    {intl.formatMessage(typeMessages[type])}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            {values.company.id ? (
              <MultiUserAutocomplete
                id="users"
                type={EUserType.Cashier}
                selectedUsers={values.users as GTUserSuggestion[]}
                companyId={values.company.id}
                onChange={(newUsers: GTUserSuggestion[]): void => {
                  setFieldValue('users', newUsers)
                }}
                onBlur={handleBlur}
                isAssigned={false}
                isActive
              />
            ) : (
              <div>
                <Label>
                  <FormattedMessage id="dictionary.users" defaultMessage="Users" />
                </Label>
                <FormattedMessage id="sites.form.selectCompany" defaultMessage="Select company" />
              </div>
            )}
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              {isAddressOptional ? (
                <FormattedMessage
                  id="site.form.address.optional"
                  defaultMessage="Address <small>(Optional)</small>"
                  values={{
                    small: (msg) => <small>{msg}</small>
                  }}
                />
              ) : (
                <FormattedMessage id="dictionary.address" defaultMessage="Address" />
              )}
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="address.line1"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line1}
              placeholder={intl.formatMessage(messages.line1Placeholder)}
              touched={touched.address && touched.address.line1}
              errors={errors.address && errors.address.line1}
              label={<FormattedMessage id="sites.new.form.address.line1.label" defaultMessage="Line 1" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="address.line2"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line2}
              placeholder={intl.formatMessage(messages.line2Placeholder)}
              touched={touched.address && touched.address.line2}
              errors={errors.address && errors.address.line2}
              label={
                isAddressOptional ? (
                  <FormattedMessage id="sites.new.form.address.line2.label" defaultMessage="Line 2" />
                ) : (
                  <FormattedMessage
                    id="sites.new.form.address.line2.optional.label"
                    defaultMessage="Line 2 <small>(Optional)</small>"
                    values={{
                      small: (msg) => <small>{msg}</small>
                    }}
                  />
                )
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="address.city"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.city}
              placeholder={intl.formatMessage(messages.cityPlaceholder)}
              touched={touched.address && touched.address.city}
              errors={errors.address && errors.address.city}
              label={<FormattedMessage id="sites.new.form.address.city.label" defaultMessage="City" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="address.zipCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.zipCode}
              placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
              touched={touched.address && touched.address.zipCode}
              errors={errors.address && errors.address.zipCode}
              label={<FormattedMessage id="sites.new.form.address.zipCode.label" defaultMessage="ZIP code" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectState
              id="address.stateCode"
              value={values.address.stateCode}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.address && touched.address.stateCode}
              errors={errors.address && errors.address.stateCode}
              label={<FormattedMessage id="dictionary.state" defaultMessage="State" />}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="site.settings.snacks.title" defaultMessage="Snacks" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.settings.snacks.sale" defaultMessage="Sell snacks" />
            </Label>
            <Switch
              value={values.settings.snacks.canSell}
              onChange={(value: boolean): void => {
                setFieldValue('settings.snacks.canSell', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.settings.snacks.showMinPrice" defaultMessage="Use minimum price" />
            </Label>
            <Switch
              value={values.settings.snacks.showMinPrice}
              onChange={(value: boolean): void => {
                setFieldValue('settings.snacks.showMinPrice', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.settings.snacks.tripRequired" defaultMessage="Require trip selection" />
            </Label>
            <Switch
              value={values.settings.snacks.tripRequired}
              onChange={(value: boolean): void => {
                if (values.type !== TYPE.FIXED) {
                  setFieldValue('settings.snacks.tripRequired', value)
                }
              }}
              disabled={values.type === TYPE.FIXED}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="site.settings.kredits.title" defaultMessage="Kredits" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.settings.kredits.topUp" defaultMessage="Top up" />
            </Label>
            <Switch
              value={values.settings.kredits.canTopUp}
              onChange={(value: boolean): void => {
                setFieldValue('settings.kredits.canTopUp', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="dictionary.settings" defaultMessage="Settings (Optional)" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column>
            <PlaceAutocomplete
              id="settings.defaultOrigin.name"
              placeholder={intl.formatMessage(messages.defaultOriginPlaceholder)}
              value={values.settings.defaultOrigin.name}
              label={<FormattedMessage id="dictionary.defaultOrigin" defaultMessage="Default Origin" />}
              onChange={(place: Pick<IPlace, 'id' | 'name'>): void => {
                setFieldValue('settings.defaultOrigin', place)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.form.passengerNameRequired" defaultMessage="Require passenger name" />
            </Label>
            <Switch
              value={values.settings.passengerNameRequired}
              onChange={(value: boolean): void => {
                setFieldValue('settings.passengerNameRequired', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.form.printingTickets" defaultMessage="Printing tickets" />
            </Label>
            <Switch
              value={values.settings.hasPrinter}
              onChange={(value: boolean): void => {
                setFieldValue('settings.hasPrinter', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectTags
              id="settings.payment"
              values={values.settings.payment}
              items={getFilteredPaymentMethod(values.settings.payment)}
              getTagLabel={(value: EPaymentMethod): string => {
                return intl.formatMessage(paymentMethodMessages[value])
              }}
              onChange={(values: EPaymentMethod[]): void => {
                setFieldTouched('settings.payment', true)
                setFieldValue('settings.payment', values)
              }}
              touched={touched.settings && touched.settings.payment}
              errors={errors.settings && errors.settings.payment}
              label={<FormattedMessage id="dictionary.selectPayment" defaultMessage="Confirmation modalities" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Select
              id="settings.saleClosingThreshold"
              value={values.settings.saleClosingThreshold}
              onChange={({ target: { value } }: ChangeEvent<HTMLSelectElement>): void => {
                setFieldValue('settings.saleClosingThreshold', parseInt(value))
              }}
              label={<FormattedMessage id="sites.form.salesClosed" defaultMessage="Sales closed" />}
            >
              <option value={0}>{intl.formatMessage(closingThresholdMessages['0'])}</option>
              <option value={1}>{intl.formatMessage(closingThresholdMessages['1'])}</option>
              <option value={5}>{intl.formatMessage(closingThresholdMessages['5'])}</option>
              <option value={10}>{intl.formatMessage(closingThresholdMessages['10'])}</option>
            </Select>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.form.modifyPrices" defaultMessage="Modify prices" />
            </Label>
            <Switch
              value={values.settings.canChangePrice}
              onChange={(value: boolean): void => {
                setFieldValue('settings.canChangePrice', value)
              }}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="sites.form.hideSales" defaultMessage="Hide sales" />
            </Label>
            <Switch
              value={values.settings.hideTransactionsReport}
              onChange={(value: boolean): void => {
                setFieldValue('settings.hideTransactionsReport', value)
              }}
            />
          </Column>
        </Row>
      </Card.Content>

      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="sites.form.error"
                defaultMessage="There was an error while saving your point of sale"
              />
            </ErrorMessage>
          </div>
        )}

        <Card.FooterActions>
          <Button as={NavLink} to="/sites" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewSite = ({ site, isEdit, onSubmit, error, loading }: INewSiteProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const address = site?.address
  const settings = site?.settings

  const initialAddress = {
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    zipCode: address?.zipCode || '',
    stateCode: address?.stateCode || ''
  }

  let paymentMethods = settings?.payment ? [...settings.payment] : PAYMENT_METHOD_LIST
  if (settings?.canReserveTickets && !paymentMethods.includes(EPaymentMethod.RESERVED)) {
    paymentMethods.push(EPaymentMethod.RESERVED)
  }
  if (settings?.enableMixedPayments && !paymentMethods.includes(EPaymentMethod.MIXED)) {
    paymentMethods.push(EPaymentMethod.MIXED)
  }
  paymentMethods = paymentMethods.map((method) => method)

  const initialSite = {
    name: site?.name || '',
    company: site?.company || getDefaultCompanyForForm(login.accessToken),
    type: site?.type || '',
    users: site?.users || [],
    address: initialAddress,
    settings: {
      snacks: !isEmpty(settings?.snacks)
        ? {
            canSell: !!settings?.snacks?.canSell,
            showMinPrice: !!settings?.snacks?.showMinPrice,
            tripRequired: !!settings?.snacks?.tripRequired
          }
        : {
            canSell: true,
            showMinPrice: false,
            tripRequired: true
          },
      kredits: !isEmpty(settings?.kredits)
        ? {
            canTopUp: !!settings?.kredits?.canTopUp
          }
        : {
            canTopUp: false
          },
      defaultOrigin: settings?.defaultOrigin || { name: '', id: '' },
      passengerNameRequired: isBoolean(settings?.passengerNameRequired) ? settings?.passengerNameRequired : true,
      hasPrinter: settings?.hasPrinter || false,
      canChangePrice: settings?.canChangePrice || false,
      saleClosingThreshold: isNumber(settings?.saleClosingThreshold) ? settings?.saleClosingThreshold : 5,
      payment: paymentMethods,
      enabledCompanyLineIDs:
        (settings?.enabledCompanyLines && settings?.enabledCompanyLines.map(({ id }: { id: string }): string => id)) ||
        [],
      hideTransactionsReport: settings?.hideTransactionsReport || false
    }
  }

  return (
    // @ts-expect-error FIXME
    <Formik initialValues={initialSite} validationSchema={schema(intl, isEdit)} onSubmit={onSubmit}>
      {(props: FormikProps<IFormikSite>): ReactElement => {
        return <NewSiteForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
